import { useState, useEffect } from "react";
import { Button, Form, Modal, ListGroup } from "react-bootstrap";
import { createNewProduct } from "../../apiForNewProducts";

export default function CreateNewProduct({
    show,
    onHide,
    jwt,
    onCreated,
    categoryId,
    categories = [],
    places = [],
    products = []  // Добавляем пропс для существующих продуктов
}) {
    const [product, setProduct] = useState({
        name: '',
        coefficient: 1,
        categoryId: categoryId,
        placeIds: [],
        priority: 0,
        productType: 0
    });

    const [isSubmitting, setIsSubmitting] = useState(false);

    // Функция для вычисления следующего приоритета при изменении категории
    useEffect(() => {
        if (products && products.length > 0) {
            // Фильтруем продукты по выбранной категории (или без категории)
            const productsInSameCategory = products.filter(p =>
                p.categoryId === (product.categoryId || null)
            );

            if (productsInSameCategory.length > 0) {
                // Находим максимальный приоритет в этой категории
                const maxPriority = Math.max(...productsInSameCategory.map(p => p.priority || 0));
                // Устанавливаем приоритет на 1 больше максимального
                setProduct(prev => ({ ...prev, priority: maxPriority + 1 }));
            } else {
                // Если в категории нет продуктов, начинаем с 0
                setProduct(prev => ({ ...prev, priority: 0 }));
            }
        }
    }, [product.categoryId, products]);

    const onSubmit = async () => {
        try {
            setIsSubmitting(true);

            // Вычисляем приоритет перед отправкой (на всякий случай)
            const productsInSameCategory = products.filter(p =>
                p.categoryId === (product.categoryId || null)
            );

            let finalPriority = product.priority;
            if (productsInSameCategory.length > 0) {
                const maxPriority = Math.max(...productsInSameCategory.map(p => p.priority || 0));
                finalPriority = maxPriority + 1;
            }

            const createdProduct = await createNewProduct({
                name: product.name,
                coefficient: product.coefficient,
                categoryId: product.categoryId,
                placeIds: product.placeIds,
                priority: finalPriority,
                productType: product.productType
            }, jwt);

            if (createdProduct) {
                const productWithPlaces = {
                    ...createdProduct,
                    places: product.placeIds.map(id => ({
                        productPlaceId: id,
                        name: places.find(p => p.id === id)?.name || ''
                    })),
                    priority: finalPriority  // Сохраняем приоритет
                };
                onCreated(productWithPlaces);
                resetForm();
            }
        } catch (error) {
            console.error('Error creating product:', error);
            alert('Не удалось создать товар');
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetForm = () => {
        setProduct({
            name: '',
            coefficient: 1,
            categoryId: categoryId,
            placeIds: [],
            priority: 0
        });
    };

    const togglePlaceSelection = (placeId) => {
        setProduct(prev => ({
            ...prev,
            placeIds: prev.placeIds.includes(placeId)
                ? prev.placeIds.filter(id => id !== placeId)
                : [...prev.placeIds, placeId]
        }));
    };

    return (
        <Modal centered show={show} onHide={() => { resetForm(); onHide(); }}>
            <Form onSubmit={e => { e.preventDefault(); onSubmit(); }}>
                <Modal.Header closeButton>
                    <Modal.Title>Новый товар</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Название</Form.Label>
                        <Form.Control
                            required
                            type="text"
                            value={product.name}
                            onChange={(e) => setProduct({ ...product, name: e.target.value })}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Коэффициент</Form.Label>
                        <Form.Control
                            required
                            type="number"
                            min="0"
                            step="0.01"
                            value={product.coefficient}
                            onChange={(e) => setProduct({
                                ...product,
                                coefficient: parseFloat(e.target.value) || 0
                            })}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Категория</Form.Label>
                        <Form.Select
                            value={product.categoryId || ''}
                            onChange={(e) => setProduct({
                                ...product,
                                categoryId: e.target.value ? parseInt(e.target.value) : null
                            })}
                        >
                            <option value="">Без категории</option>
                            {categories.map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Тип продукта</Form.Label>
                        <Form.Select
                            value={product.productType}
                            onChange={(e) => setProduct({ ...product, productType: parseInt(e.target.value) })}
                        >
                            <option value={0}>Изделие</option>
                            <option value={1}>Допик</option>
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Маркетплейсы (выберите где будет доступен товар)</Form.Label>
                        <ListGroup>
                            {places.map(place => (
                                <ListGroup.Item
                                    key={place.id}
                                    action
                                    active={product.placeIds.includes(place.id)}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        togglePlaceSelection(place.id)
                                    }}
                                >
                                    {place.name}
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                        <Form.Text className="text-muted">
                            Если не выбрать ни один маркетплейс, товар будет доступен на всех
                        </Form.Text>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => { resetForm(); onHide(); }} disabled={isSubmitting}>
                        Отмена
                    </Button>
                    <Button variant="success" type="submit" disabled={isSubmitting}>
                        {isSubmitting ? 'Создание...' : 'Создать'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}